import { Users } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const testSocijalneAnksioznosti: QuizConfig = {
  id: "test-socijalne-anksioznosti",
  illustration: {
    src: "/images/quizzes/test-socijalne-anksioznosti.webp",
    alt: "Muškarac na druženju okleva da se uključi u razgovor.",
  },
  lastModified: "2026-07-18",
  title: "Test socijalne anksioznosti: Koliko te strah od ljudi ograničava?",
  shortTitle: "Test socijalne anksioznosti",
  description:
    "Besplatan test socijalne anksioznosti - otkrij koliko te strah od društvenih situacija i ljudi ograničava u svakodnevnom životu.",
  longDescription:
    "Ovaj test analizira tvoj strah od društvenih situacija - kako se osećaš kada treba da pričaš sa ljudima, kako reaguješ na javne nastupe i koliko izbegavaš društvene događaje. Saznaćeš da li je tvoja socijalna anksioznost blaga, umerena ili ozbiljna, koje situacije te najviše pogađaju i kako se nosiš sa strahom od osude drugih. Na kraju dobijaš personalizovane savete za prevazilaženje socijalne anksioznosti i konkretne tehnike koje možeš primeniti u društvenim situacijama.",
  icon: Users,
  duration: "4-5 min",
  category: "Mentalno zdravlje",
  focusAreasWanted: [
    "social_anxiety",
    "anxiety",
    "emotional_regulation",
    "shame_guilt",
  ],
  questions: [
    {
      type: "radio",
      question: "Koliko često osećaš napetost ili strah kada treba da pričaš sa nepoznatim ljudima?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Skoro uvek",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Koje situacije te najviše plaše? (označi sve što važi)",
      options: [
        "Javni nastupi i prezentacije",
        "Druženje na većim okupljanjima",
        "Razgovor sa autoritetima (šef, profesor)",
        "Pozivanje ljudi na dejt",
        "Prijavljenje u prodavnici ili restoranu",
        "Razgovor telefonom",
        "Ništa od ovoga",
      ],
      scores: [1, 1, 1, 1, 1, 1, 0],
    },
    {
      type: "radio",
      question: "Koliko često izbegavaš društvene situacije zbog straha?",
      options: [
        "Nikad ne izbegavam",
        "Retko izbegavam",
        "Ponekad izbegavam",
        "Često izbegavam",
        "Skoro uvek izbegavam",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako se osećaš kada misliš da te drugi posmatraju ili procenjuju?",
      options: [
        "Potpuno opušteno",
        "Malo nelagodno",
        "Dosta nelagodno",
        "Veoma nelagodno",
        "Panika me hvata",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Koje fizičke simptome osećaš u društvenim situacijama? (označi sve što važi)",
      options: [
        "Crvenim se u licu",
        "Znojiš se",
        "Drhtiš ili treseš se",
        "Srce ti ubrzano lupa",
        "Teško dišeš",
        "Muka ti je",
        "Ništa od ovoga",
      ],
      scores: [1, 1, 1, 1, 1, 1, 0],
    },
    {
      type: "radio",
      question: "Koliko te briga šta drugi misle o tebi?",
      options: [
        "Uopšte me ne brine",
        "Malo me brine",
        "Umereno me brine",
        "Dosta me brine",
        "Previše me brine",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako reaguješ kada napraviš grešku pred drugima?",
      options: [
        "Ne brinem se, greške su normalne",
        "Malo me muči, ali prođe",
        "Dugo razmišljam o tome",
        "Baš me pogodi i muči me",
        "Danima se loše osećam zbog toga",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Koje misli te muče u društvenim situacijama? (označi sve što važi)",
      options: [
        "Strah da ću reći nešto glupo",
        "Briga da ću biti osuđen/a",
        "Strah da ću biti odbačen/a",
        "Briga da ću biti smešan/a",
        "Strah da ću biti dosadan/a",
        "Nemam takve misli",
      ],
      scores: [1, 1, 1, 1, 1, 0],
    },
    {
      type: "radio",
      question: "Koliko ti je teško da započneš razgovor sa nepoznatim ljudima?",
      options: [
        "Uopšte mi nije teško",
        "Malo mi je teško",
        "Umereno mi je teško",
        "Dosta mi je teško",
        "Veoma mi je teško",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često se osećaš usamljeno zbog izbegavanja društvenih situacija?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Skoro uvek",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "context",
      question: "Šta ti pomaže kada si u društvenim situacijama? (označi sve što važi)",
      options: [
        "Priprema unapred šta ću reći",
        "Dublje disanje",
        "Razgovor sa bliskom osobom pre događaja",
        "Alkohol ili druge supstance",
        "Izbegavanje kontakta očima",
        "Ništa mi posebno ne pomaže",
      ],
      scores: [1, 1, 1, 1, 1, 0],
    },
    {
      type: "textarea",
      question: "Opiši kako socijalna anksioznost utiče na tvoj život:",
    },
  ],
  copy: {
    title: "Tvoj nivo socijalne anksioznosti",
    subtitle: "Razumevanje tvog straha od društvenih situacija",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Saveti za prevazilaženje socijalne anksioznosti",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
  promptExtension: `
    ## Specific Instructions for Social Anxiety Test

    - Higher scores indicate more severe social anxiety. Interpret scores as follows:
      - 0-20%: Minimal social anxiety - user is comfortable in social situations
      - 21-40%: Mild social anxiety - some discomfort but manageable
      - 41-60%: Moderate social anxiety - significant impact on daily life
      - 61-80%: Severe social anxiety - major limitations in social situations
      - 81-100%: Very severe social anxiety - may need professional help

    - Focus on specific social situations that trigger anxiety (public speaking, dating, small talk, etc.)
    - Address physical symptoms (blushing, sweating, trembling) and how they affect the user
    - Discuss avoidance behaviors and their impact on relationships and opportunities
    - Provide actionable strategies for gradual exposure to feared situations
    - Emphasize that social anxiety is treatable and many people overcome it
    - If score is above 60%, strongly recommend professional therapy as social anxiety responds well to CBT and exposure therapy
  `,
};
